"use client";

import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { createClient } from "@/lib/supabase/client";

type WorkspaceSetupModalProps = {
  isOpen: boolean;
  onClose: () => void;
  currentName: string;
  workspaceId: string;
};

export default function WorkspaceSetupModal({
  isOpen,
  onClose,
  currentName,
  workspaceId,
}: WorkspaceSetupModalProps) {
  const supabase = useMemo(() => createClient(), []);
  const [name, setName] = useState(currentName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(currentName);
  }, [currentName]);

  if (!isOpen) return null;

  const onSave = async () => {
    setError(null);
    setSaving(true);
    try {
      const { error: updateError } = await supabase
        .from("workspaces")
        .update({ name: name.trim(), name_customised: true })
        .eq("id", workspaceId);

      if (updateError) {
        setError(updateError.message);
        return;
      }

      onClose();
    } finally {
      setSaving(false);
    }
  };

  const onDontAskAgain = async () => {
    setError(null);
    setSaving(true);
    try {
      const { error: updateError } = await supabase
        .from("workspaces")
        .update({ name_customised: true })
        .eq("id", workspaceId);

      if (updateError) {
        setError(updateError.message);
        return;
      }

      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-background p-8 shadow-lg">
        <h2 className="font-display text-2xl font-medium text-foreground">
          What&apos;s your workspace called?
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          This is how your team and forms will be identified.
        </p>

        <div className="mt-6">
          <Input
            placeholder="e.g. Acme Corp"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-11"
          />
          {error ? <p className="mt-2 text-sm text-red-500">{error}</p> : null}
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <Button
            type="button"
            variant="outline"
            className="h-11"
            onClick={onClose}
            disabled={saving}
          >
            Skip for now
          </Button>
          <Button
            type="button"
            className="h-11"
            onClick={onSave}
            disabled={saving || name.trim().length === 0}
          >
            Save
          </Button>
        </div>

        <div className="mt-2 text-center">
          <button
            type="button"
            onClick={onDontAskAgain}
            disabled={saving}
            className="text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            Don&apos;t ask again
          </button>
        </div>

        <p className="mt-3 text-center text-xs text-muted-foreground">
          You can always change this in Settings
        </p>
      </div>
    </div>
  );
}

